import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Download, Trash2, CheckCircle, XCircle, Clock, Loader2, Eye, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { kycApi, ordersApi } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// India-specific document types
const INDIA_DOC_TYPES = {
  company: [
    { value: 'coi', label: 'Certificate of Incorporation (COI)' },
    { value: 'moa', label: 'Memorandum of Association (MOA)' },
    { value: 'aoa', label: 'Articles of Association (AOA)' },
    { value: 'pan_card', label: 'Company PAN Card' },
    { value: 'tan_card', label: 'TAN Card' },
    { value: 'gst_certificate', label: 'GST Certificate' },
    { value: 'msme_certificate', label: 'MSME Certificate' },
    { value: 'shop_act_license', label: 'Shop Act License' },
    { value: 'trade_license', label: 'Trade License' },
    { value: 'other_company', label: 'Other Document' },
  ],
  director: [
    { value: 'pan_card', label: 'PAN Card' },
    { value: 'aadhaar', label: 'Aadhaar Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'voter_id', label: 'Voter ID' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'photo', label: 'Passport Size Photo' },
    { value: 'address_proof', label: 'Address Proof' },
    { value: 'bank_statement', label: 'Bank Statement' },
    { value: 'dsc_certificate', label: 'DSC Certificate' },
    { value: 'din_proof', label: 'DIN Proof' },
    { value: 'other_individual', label: 'Other Document' },
  ],
  shareholder: [
    { value: 'pan_card', label: 'PAN Card' },
    { value: 'aadhaar', label: 'Aadhaar Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'voter_id', label: 'Voter ID' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'photo', label: 'Passport Size Photo' },
    { value: 'address_proof', label: 'Address Proof' },
    { value: 'other_individual', label: 'Other Document' },
  ],
};

// US-specific document types
const US_DOC_TYPES = {
  company: [
    { value: 'ein_letter', label: 'EIN Confirmation Letter (IRS)' },
    { value: 'articles_of_organization', label: 'Articles of Organization' },
    { value: 'articles_of_incorporation', label: 'Articles of Incorporation' },
    { value: 'operating_agreement', label: 'Operating Agreement' },
    { value: 'bylaws', label: 'Corporate Bylaws' },
    { value: 'certificate_of_formation', label: 'Certificate of Formation' },
    { value: 'certificate_of_good_standing', label: 'Certificate of Good Standing' },
    { value: 'business_license', label: 'Business License' },
    { value: 'registered_agent_agreement', label: 'Registered Agent Agreement' },
    { value: 'boir_confirmation', label: 'BOIR Filing Confirmation' },
    { value: 'state_tax_registration', label: 'State Tax Registration' },
    { value: 'other_company', label: 'Other Document' },
  ],
  director: [
    { value: 'ssn_card', label: 'Social Security Card (SSN)' },
    { value: 'itin_letter', label: 'ITIN Assignment Letter' },
    { value: 'passport', label: 'Passport' },
    { value: 'drivers_license', label: "Driver's License" },
    { value: 'state_id', label: 'State ID Card' },
    { value: 'photo', label: 'Passport Size Photo' },
    { value: 'address_proof', label: 'Proof of Address (Utility Bill)' },
    { value: 'w8_form', label: 'W-8BEN Form' },
    { value: 'w9_form', label: 'W-9 Form' },
    { value: 'bank_statement', label: 'Bank Statement' },
    { value: 'other_individual', label: 'Other Document' },
  ],
  shareholder: [
    { value: 'ssn_card', label: 'Social Security Card (SSN)' },
    { value: 'itin_letter', label: 'ITIN Assignment Letter' },
    { value: 'passport', label: 'Passport' },
    { value: 'drivers_license', label: "Driver's License" },
    { value: 'state_id', label: 'State ID Card' },
    { value: 'photo', label: 'Passport Size Photo' },
    { value: 'address_proof', label: 'Proof of Address (Utility Bill)' },
    { value: 'w8_form', label: 'W-8BEN Form' },
    { value: 'w9_form', label: 'W-9 Form' },
    { value: 'other_individual', label: 'Other Document' },
  ],
};

// Combined for backward compatibility (default to India)
const DOC_TYPE_OPTIONS = INDIA_DOC_TYPES;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  verified: { label: 'Verified', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  submitted: { label: 'Submitted', color: 'bg-blue-500/20 text-blue-400', icon: FileText },
};

// Safe error message extraction — prevents React crash from rendering Pydantic error objects
const getApiError = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || 'Validation error').join(', ');
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const KYCDocumentCard = ({ doc, isAdmin, onDelete, onVerify, onReupload }) => {
  const [verifying, setVerifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reuploadFile, setReuploadFile] = useState(null);
  const [reuploading, setReuploading] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const reuploadInputRef = useRef(null);
  const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const result = await kycApi.getDownloadUrl(doc.id);
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      }
    } catch (error) {
      const token = localStorage.getItem('token');
      const url = `${API_URL}${doc.document_url}${doc.document_url.includes('?') ? '&' : '?'}token=${token}`;
      window.open(url, '_blank');
      if (error.response?.data?.detail) {
        toast.error(getApiError(error, 'Download failed'));
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleView = async () => {
    setDownloading(true);
    try {
      const result = await kycApi.getDownloadUrl(doc.id);
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Document not found. It may have been deleted or not uploaded correctly.');
      } else {
        toast.error(getApiError(error, 'Failed to load document. Please try again.'));
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleVerify = async (status, notes = null) => {
    setVerifying(true);
    try {
      await onVerify(doc.id, status, notes);
      setShowRejectForm(false);
      setRejectNotes('');
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    setDeleting(true);
    try {
      await onDelete(doc.id);
    } finally {
      setDeleting(false);
    }
  };

  const handleReuploadSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReuploadFile(file);
    }
  };

  const handleReuploadSubmit = async () => {
    if (!reuploadFile || !onReupload) return;
    setReuploading(true);
    try {
      await onReupload(doc.document_type, doc.document_name, reuploadFile);
      setReuploadFile(null);
      if (reuploadInputRef.current) reuploadInputRef.current.value = '';
    } finally {
      setReuploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isCloudStored = doc.storage_type === 's3' || doc.s3_key;
  const isRejected = doc.status === 'rejected';

  return (
    <div className={`bg-slate-900/50 border rounded-lg p-4 ${isRejected ? 'border-red-500/30' : 'border-slate-700/30'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-indigo-400 flex-shrink-0" />
            <h4 className="text-white font-medium truncate">{doc.document_name}</h4>
          </div>
          <p className="text-xs text-slate-500 mb-2">{doc.file_name} {formatFileSize(doc.file_size)}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
            {isCloudStored && (
              <span className="px-2 py-0.5 rounded text-xs bg-cyan-500/20 text-cyan-400">
                Cloud
              </span>
            )}
            {doc.uploaded_by_name && (
              <span className="text-xs text-slate-500">
                by {doc.uploaded_by_name}
                {doc.uploaded_by_role && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                    doc.uploaded_by_role.includes('admin') ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {doc.uploaded_by_role.includes('admin') ? 'Admin' : 'Client'}
                  </span>
                )}
              </span>
            )}
          </div>
          {doc.verification_notes && (
            <p className="text-xs text-red-300 mt-2 italic bg-red-500/10 px-2 py-1 rounded">Reason: {doc.verification_notes}</p>
          )}

          {/* Re-upload section — shown for rejected docs when user is NOT admin */}
          {isRejected && !isAdmin && onReupload && (
            <div className="mt-3 pt-3 border-t border-red-500/20" data-testid={`reupload-section-${doc.id}`}>
              <p className="text-xs text-red-400 mb-2 font-medium">This document was rejected. Please upload a corrected version:</p>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="file"
                  ref={reuploadInputRef}
                  onChange={handleReuploadSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                />
                <Button variant="outline" size="sm" onClick={() => reuploadInputRef.current?.click()}
                  className="border-red-500/40 text-red-300 hover:bg-red-900/30 text-xs">
                  <Upload className="w-3 h-3 mr-1" /> Choose New File
                </Button>
                {reuploadFile && (
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {reuploadFile.name}
                  </span>
                )}
                {reuploadFile && (
                  <Button size="sm" onClick={handleReuploadSubmit} disabled={reuploading}
                    className="bg-red-600 hover:bg-red-700 text-xs">
                    {reuploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                    Re-upload
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <button 
            onClick={handleView}
            disabled={downloading}
            className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
            title="View Document"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          </button>
          <button 
            onClick={handleDownload}
            disabled={downloading}
            className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
            title="Download Document"
          >
            <Download className="w-4 h-4" />
          </button>
          
          {isAdmin && (
            <>
              {doc.status !== 'verified' && (
                <button onClick={() => handleVerify('verified')} disabled={verifying}
                  className="p-2 rounded hover:bg-emerald-900/50 text-emerald-400 transition-colors disabled:opacity-50"
                  title="Approve Document">
                  <CheckCircle className="w-4 h-4" />
                </button>
              )}
              {doc.status !== 'rejected' && (
                <button onClick={() => setShowRejectForm(true)} disabled={verifying}
                  className="p-2 rounded hover:bg-red-900/50 text-red-400 transition-colors disabled:opacity-50"
                  title="Reject Document"
                  data-testid={`reject-btn-${doc.id}`}>
                  <XCircle className="w-4 h-4" />
                </button>
              )}
              <button onClick={handleDelete} disabled={deleting}
                className="p-2 rounded hover:bg-red-900/50 text-red-400 transition-colors disabled:opacity-50"
                title="Delete Document">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Rejection notes form — shown when admin clicks reject */}
      {showRejectForm && isAdmin && (
        <div className="mt-3 pt-3 border-t border-red-500/20 bg-red-950/20 rounded-b-lg px-4 pb-4" data-testid={`reject-form-${doc.id}`}>
          <p className="text-xs text-red-400 font-medium mb-2">Rejection Reason (visible to client):</p>
          <textarea
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            placeholder="e.g. Document is blurry, wrong name, expired ID..."
            className="w-full rounded-md bg-slate-800 border border-red-500/30 text-white text-sm p-2 mb-2 placeholder:text-slate-500 focus:ring-red-500 focus:border-red-500 resize-none"
            rows={2}
            data-testid={`reject-notes-input-${doc.id}`}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => handleVerify('rejected', rejectNotes || null)} disabled={verifying}
              className="bg-red-600 hover:bg-red-700 text-xs" data-testid={`reject-confirm-btn-${doc.id}`}>
              {verifying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
              Confirm Rejection
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setShowRejectForm(false); setRejectNotes(''); }}
              className="text-slate-400 hover:text-white text-xs">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const KYCDocuments = ({ 
  entityType, // 'company', 'director', 'shareholder'
  entityId,
  entityName,
  isAdmin = false,
  onRefresh,
  country = 'IN', // 'IN' for India, 'US' for United States
  orderId = null,  // When provided, enables smart KYC gap filtering
  orderStatus = null  // When provided, gates the upload reminder so it hides once order is past documents_pending
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('');
  const [docName, setDocName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Smart KYC gap state
  const [gapData, setGapData] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);

  // Get document types based on country (full list — used as fallback)
  const getDocTypeOptions = () => {
    const docTypes = country === 'US' ? US_DOC_TYPES : INDIA_DOC_TYPES;
    return docTypes[entityType] || [];
  };

  const fullDocTypeOptions = getDocTypeOptions();

  // Compute the filtered dropdown options — show docs that need upload OR re-upload
  const docTypeOptions = (() => {
    if (!orderId) return fullDocTypeOptions;
    if (!gapData) return [];
    if (!gapData.kyc_required) return fullDocTypeOptions;
    // Include both pending AND rejected docs in dropdown for re-upload
    const needsUploadSlugs = new Set(
      (gapData.documents || [])
        .filter(d => d.entity_type === entityType && (d.status === 'pending' || d.status === 'rejected'))
        .map(d => d.slug)
    );
    // Also include doc types from locally rejected documents (per-entity)
    documents.filter(d => d.status === 'rejected').forEach(d => {
      needsUploadSlugs.add(d.document_type);
    });
    return fullDocTypeOptions.filter(opt => needsUploadSlugs.has(opt.value));
  })();

  // All configured docs for this entity type (for checklist display)
  const configuredDocs = orderId && gapData?.kyc_required
    ? (gapData.documents || []).filter(d => d.entity_type === entityType)
    : [];

  // Fetch KYC gap when orderId is provided
  const loadGap = async () => {
    if (!orderId) return;
    setGapLoading(true);
    try {
      const data = await ordersApi.getKycGap(orderId, entityType, entityId);
      setGapData(data);
    } catch (error) {
      console.error('Failed to load KYC gap:', error);
      setGapData(null); // Fallback: show full list
    } finally {
      setGapLoading(false);
    }
  };

  useEffect(() => {
    if (entityId) {
      loadDocuments();
    }
  }, [entityId, entityType]);

  useEffect(() => {
    loadGap();
  }, [orderId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      let docs;
      if (entityType === 'company') {
        docs = await kycApi.getCompanyDocuments(entityId);
      } else if (entityType === 'director') {
        docs = await kycApi.getDirectorDocuments(entityId);
      } else if (entityType === 'shareholder') {
        docs = await kycApi.getShareholderDocuments(entityId);
      }
      setDocuments(docs || []);
    } catch (error) {
      console.error('Failed to load KYC documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill document name if empty
      if (!docName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setDocName(nameWithoutExt);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !docType || !docName) {
      toast.error('Please select a document type, name, and file');
      return;
    }

    setUploading(true);
    try {
      let result;
      if (entityType === 'company') {
        result = await kycApi.uploadCompanyDocument(entityId, docType, docName, description, selectedFile);
      } else if (entityType === 'director') {
        result = await kycApi.uploadDirectorDocument(entityId, docType, docName, description, selectedFile);
      } else if (entityType === 'shareholder') {
        result = await kycApi.uploadShareholderDocument(entityId, docType, docName, description, selectedFile);
      }
      
      toast.success('Document uploaded successfully');
      setDocType('');
      setDocName('');
      setDescription('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadDocuments();
      loadGap(); // Re-fetch gap after upload
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await kycApi.deleteDocument(docId);
      toast.success('Document deleted');
      loadDocuments();
      loadGap(); // Re-fetch gap after delete
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleVerify = async (docId, status, notes = null) => {
    try {
      await kycApi.verifyDocument(docId, status, notes);
      toast.success(`Document ${status === 'verified' ? 'verified' : 'rejected'}`);
      loadDocuments();
      loadGap();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update document status');
    }
  };

  const verifiedCount = documents.filter(d => d.status === 'verified').length;
  const pendingCount = documents.filter(d => d.status === 'pending' || d.status === 'submitted').length;
  const rejectedCount = documents.filter(d => d.status === 'rejected').length;

  // ---- Smart KYC scenario detection ----
  const hasGap = orderId && gapData;
  const isNoReqsConfigured = hasGap && !gapData.kyc_required;
  const isAllVerified = hasGap && gapData.kyc_required && gapData.all_verified;
  const hasGapItems = hasGap && gapData.kyc_required && !gapData.all_verified;
  // Does this entity_type have any configured items?
  const entityHasConfigured = configuredDocs.length > 0;
  // Treat rejected docs the same as pending — they need re-upload
  const entityHasPending = configuredDocs.some(d => d.status === 'pending' || d.status === 'rejected');
  const entityHasRejected = configuredDocs.some(d => d.status === 'rejected') || rejectedCount > 0;

  // Should we show the upload section?
  // - Always show for admins (they may need to upload on behalf of client at any stage)
  // - Always show for rejected docs (re-upload path stays open even if order is under_review)
  // - For clients: allow upload as long as the order is NOT completed/cancelled — clients
  //   may need to submit KYC before payment (draft/invoice_requested/payment_pending),
  //   during doc collection, or even later (under_review/signature_*) if extra docs are needed.
  const TERMINAL_STATUSES = ['completed', 'cancelled'];
  const orderIsInUploadPhase = !orderId || !orderStatus || !TERMINAL_STATUSES.includes(orderStatus);
  const showUploadSection = isAdmin
    || entityHasRejected
    || (orderIsInUploadPhase && (!hasGap || (hasGapItems && entityHasPending)));

  // Re-upload handler — called from rejected document cards
  const handleReupload = async (docType, docName, file) => {
    setUploading(true);
    try {
      if (entityType === 'company') {
        await kycApi.uploadCompanyDocument(entityId, docType, docName, '', file);
      } else if (entityType === 'director') {
        await kycApi.uploadDirectorDocument(entityId, docType, docName, '', file);
      } else if (entityType === 'shareholder') {
        await kycApi.uploadShareholderDocument(entityId, docType, docName, '', file);
      }
      toast.success('Document re-uploaded successfully');
      loadDocuments();
      loadGap();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to re-upload document');
    } finally {
      setUploading(false);
    }
  };

  // Compute overall KYC status label
  const getOverallStatus = () => {
    if (isAllVerified) return { label: 'All Verified', color: 'bg-emerald-500/20 text-emerald-400' };
    if (verifiedCount > 0 && (rejectedCount > 0 || pendingCount > 0)) return { label: 'Partial', color: 'bg-amber-500/20 text-amber-400' };
    if (rejectedCount > 0 && verifiedCount === 0) return { label: 'Action Required', color: 'bg-red-500/20 text-red-400' };
    return null;
  };
  const overallStatus = getOverallStatus();

  // While gap is loading, don't show the upload section yet (prevent flash of full dropdown)
  if (orderId && gapLoading) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-indigo-400" />
          <h3 className="text-lg font-semibold text-white">KYC Documents</h3>
        </div>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-400 mr-2" />
          <span className="text-sm text-slate-400">Checking document requirements...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-400" />
            KYC Documents
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              country === 'US' 
                ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' 
                : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
            }`}>
              {country === 'US' ? 'US' : 'India'}
            </span>
          </h3>
          <p className="text-sm text-slate-400 mt-0.5">
            {entityName ? `${entityName} - ` : ''}
            {documents.length} documents ({verifiedCount} verified{rejectedCount > 0 ? `, ${rejectedCount} rejected` : ''}{pendingCount > 0 ? `, ${pendingCount} pending` : ''})
            {overallStatus && (
              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${overallStatus.color}`} data-testid="kyc-overall-status">
                {overallStatus.label}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Scenario 1: No KYC requirements configured */}
      {isNoReqsConfigured && (
        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/30 text-center" data-testid="kyc-no-requirements">
          <p className="text-sm text-slate-400">No KYC documents are required for this product.</p>
        </div>
      )}

      {/* Rejected documents alert — show when any docs are rejected */}
      {entityHasRejected && !isAdmin && (
        <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30 mb-4" data-testid="kyc-rejected-alert">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-400 font-medium">Documents need attention</p>
              <p className="text-xs text-red-300/70 mt-0.5">
                {rejectedCount} document{rejectedCount !== 1 ? 's were' : ' was'} rejected. Please review the rejection reason and re-upload corrected versions below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Required Documents Checklist — shows all configured docs with status */}
      {entityHasConfigured && (
        <div className="mb-4" data-testid="kyc-checklist">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Required Documents</p>
          <div className="space-y-1.5">
            {configuredDocs.map((d, i) => {
              const statusStyles = {
                pending:  { icon: <div className="w-4 h-4 rounded-full border-2 border-amber-500 flex-shrink-0" />, badge: 'bg-amber-500/15 text-amber-400', label: 'Pending' },
                uploaded: { icon: <Clock className="w-4 h-4 text-blue-400 flex-shrink-0" />, badge: 'bg-blue-500/15 text-blue-400', label: 'Uploaded' },
                verified: { icon: <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />, badge: 'bg-emerald-500/15 text-emerald-400', label: 'Verified' },
                rejected: { icon: <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />, badge: 'bg-red-500/15 text-red-400', label: 'Rejected' },
              };
              const s = statusStyles[d.status] || statusStyles.pending;
              return (
                <div key={d.slug || i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-900/40" data-testid={`kyc-req-${d.slug}`}>
                  <div className="flex items-center gap-2.5">
                    {s.icon}
                    <span className="text-sm text-slate-200">{d.name}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${s.badge}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scenario 2: All required KYC verified */}
      {isAllVerified && entityHasConfigured && (
        <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/30 mb-4" data-testid="kyc-all-verified">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <p className="text-sm text-emerald-400">All required documents verified</p>
          </div>
        </div>
      )}

      {/* Upload Section — show when pending or rejected docs exist */}
      {showUploadSection && (
        <>
          <div className="bg-slate-900/50 rounded-lg p-4 mb-4 border border-dashed border-indigo-500/50">
            <p className="text-sm text-indigo-400 font-medium mb-3">
              {entityHasRejected ? 'Upload / Re-upload Document' : 'Upload Document'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <select 
                value={docType} 
                onChange={(e) => setDocType(e.target.value)}
                className="w-full rounded-md bg-slate-800 border-slate-700 text-white p-2 text-sm"
                data-testid="kyc-doc-type-select"
              >
                <option value="">Select Document Type</option>
                {docTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Input 
                placeholder="Document Name"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden" 
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                className="border-slate-600 text-slate-300 hover:bg-slate-700">
                <Upload className="w-4 h-4 mr-2" /> Choose File
              </Button>
              {selectedFile && (
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {selectedFile.name}
                </span>
              )}
              <Button size="sm" onClick={handleUpload} disabled={uploading || !selectedFile || !docType || !docName}
                className="bg-indigo-600 hover:bg-indigo-700 ml-auto">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Documents List — show uploaded docs */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
        </div>
      ) : documents.length > 0 ? (
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {documents.map(doc => (
            <KYCDocumentCard 
              key={doc.id} 
              doc={doc} 
              isAdmin={isAdmin}
              onDelete={handleDelete}
              onVerify={handleVerify}
              onReupload={handleReupload}
            />
          ))}
        </div>
      ) : !isNoReqsConfigured && !isAllVerified ? (
        <p className="text-center py-6 text-slate-500 text-sm">No documents uploaded yet</p>
      ) : null}
    </div>
  );
};

export default KYCDocuments;
